import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Loader2, LogOut, AlertTriangle } from "lucide-react";
import { toast, Toaster } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/lib/api";
import SiteHeader from "@/components/SiteHeader";

export default function AdminLogin() {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const { user, loading: authLoading, login, logout } = useAuth();
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const u = await login(email, password);
            if (u.role === "store_owner") {
                toast.info("Account rivenditore — reindirizzo all'area negozio.");
                navigate("/rivenditore");
            } else {
                navigate("/admin");
            }
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || err.message);
        } finally {
            setLoading(false);
        }
    };

    // Already logged in: show context-aware actions
    const alreadyLogged = !authLoading && user;

    return (
        <div className="min-h-screen flex flex-col bg-magni-cream">
            <SiteHeader />
            <Toaster position="top-center" richColors />
            <main className="flex-1 flex items-center justify-center px-4 py-16">
                <div className="w-full max-w-md bg-white border border-magni-light p-10">
                    <div className="overline mb-2">Backoffice</div>
                    <h1 className="font-serif-display text-3xl text-magni-navy mb-2">
                        Accesso amministratore
                    </h1>
                    <p className="text-sm text-magni-muted mb-8">
                        Gestione negozi, disponibilità e prenotazioni MagniStretch® Trial.
                    </p>

                    {alreadyLogged && user.role === "admin" && (
                        <div className="bg-magni-celeste border border-magni-light p-4 mb-6 text-sm">
                            Sei già loggato come <strong>{user.email}</strong> (admin).
                            <div className="mt-3 flex gap-3">
                                <button
                                    onClick={() => navigate("/admin")}
                                    className="magni-btn-primary"
                                    style={{ padding: "10px 18px", fontSize: "12px" }}
                                >
                                    Vai al backoffice
                                </button>
                                <button
                                    onClick={async () => { await logout(); }}
                                    className="magni-btn-ghost"
                                >
                                    <LogOut className="w-4 h-4 mr-1" /> Esci
                                </button>
                            </div>
                        </div>
                    )}

                    {alreadyLogged && user.role === "store_owner" && (
                        <div className="bg-yellow-50 border border-yellow-200 p-4 mb-6 text-sm text-magni-navy">
                            <div className="flex items-start gap-2 mb-2">
                                <AlertTriangle className="w-4 h-4 mt-0.5 text-yellow-600 flex-shrink-0" />
                                <div>
                                    Sei già loggato come <strong>{user.email}</strong> (rivenditore).
                                    Per accedere come amministratore esci prima.
                                </div>
                            </div>
                            <div className="flex gap-3">
                                <button
                                    onClick={() => navigate("/rivenditore")}
                                    className="magni-btn-secondary"
                                    style={{ padding: "10px 18px", fontSize: "12px" }}
                                >
                                    Area rivenditore
                                </button>
                                <button
                                    onClick={async () => { await logout(); }}
                                    className="magni-btn-primary"
                                    style={{ padding: "10px 18px", fontSize: "12px" }}
                                >
                                    <LogOut className="w-4 h-4 mr-1" /> Esci e cambia account
                                </button>
                            </div>
                        </div>
                    )}

                    {!alreadyLogged && (
                        <form onSubmit={handleSubmit} className="space-y-5">
                            <div>
                                <label className="text-xs uppercase tracking-[0.15em] font-semibold text-magni-navy block mb-2">
                                    Email
                                </label>
                                <input
                                    data-testid="admin-email-input"
                                    type="email"
                                    required
                                    className="magni-input"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    autoFocus
                                />
                            </div>
                            <div>
                                <label className="text-xs uppercase tracking-[0.15em] font-semibold text-magni-navy block mb-2">
                                    Password
                                </label>
                                <input
                                    data-testid="admin-password-input"
                                    type="password"
                                    required
                                    className="magni-input"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                />
                            </div>
                            <button
                                data-testid="admin-login-submit"
                                type="submit"
                                disabled={loading}
                                className="magni-btn-primary w-full"
                            >
                                {loading ? (
                                    <>
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Accesso...
                                    </>
                                ) : (
                                    "Accedi"
                                )}
                            </button>
                        </form>
                    )}

                    <div className="mt-6 pt-6 border-t border-magni-light text-xs text-magni-muted text-center space-y-2">
                        <div>
                            Sei un rivenditore?{" "}
                            <Link to="/rivenditore/login" className="text-magni-navy hover:underline font-semibold">
                                Accedi all'area negozio →
                            </Link>
                        </div>
                        <div>
                            <Link to="/" className="hover:text-magni-navy">
                                ← Torna al sito pubblico
                            </Link>
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
}
